import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import HandCard from '../../../respectCards/hand/HandCard';
import { actsLine, HandCardDef } from '../../../respectCards/hand/handModel';
import { SERIF, DECK_INK } from '../../../respectCards/hand/deck';
import { useGetAdminCardCatalogQuery } from '../../../respectCards/api/respectCardsApi';
import type { AdminCardRow, CardDefinition } from '../../../respectCards/types/cardTypes';
import { usePutQuizPrizeMutation } from '../../api/artOfTheDayApi';
import type { QuizPrizePreviewDto } from '../../api/quizTypes';
import { QUIZ_COLORS } from './Tiles';

/**
 * The card today's winner gets, under the guess box while the quiz runs. It is
 * drawn when the guessing opens (an admin can swap it), so everyone plays for
 * a card they can see.
 */

const Wrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin: 12px auto 2px;
  max-width: 340px;
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  max-width: 220px;
`;

const Label = styled.span`
  font-size: 0.68rem;
  font-style: italic;
  color: ${QUIZ_COLORS.label};
`;

const Name = styled.span`
  font-family: ${SERIF};
  font-style: italic;
  font-weight: 600;
  font-size: 1.05rem;
  line-height: 1.1;
  color: ${DECK_INK};
`;

const Desc = styled.span`
  font-size: 0.68rem;
  line-height: 1.35;
  color: ${QUIZ_COLORS.ink2};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Change = styled.button`
  align-self: flex-start;
  margin-top: 2px;
  padding: 0;
  border: 0;
  background: transparent;
  appearance: none;
  font-size: 0.65rem;
  color: ${QUIZ_COLORS.label};
  text-decoration: underline;
  cursor: pointer;
`;

export function prizeFace(p: QuizPrizePreviewDto): HandCardDef {
  return {
    slug: p.cardSlug,
    emoji: p.cardEmoji,
    name: p.cardName,
    rarity: p.rarity,
    text: p.description,
    when: actsLine({ effectLifetime: p.effectLifetime } as CardDefinition),
  };
}

/** Admin only: pick another card for today's winner. */
const PrizePicker: FC<{ dayKey: string; current: string; onDone: () => void }> = ({ dayKey, current, onDone }) => {
  const { data, isLoading } = useGetAdminCardCatalogQuery();
  const [setPrize, { isLoading: saving }] = usePutQuizPrizeMutation();
  const [error, setError] = useState<string | null>(null);
  const cards = (data?.cards ?? [])
    .filter((c) => (c as AdminCardRow).isActive !== false && c.slug)
    .sort((a, b) => a.name.localeCompare(b.name));

  const choose = async (slug: string) => {
    if (!slug || slug === current) return onDone();
    setError(null);
    try {
      await setPrize({ dayKey, cardSlug: slug }).unwrap();
      onDone();
    } catch (err: any) {
      setError(err?.data?.message ?? 'Could not change the prize.');
    }
  };

  return (
    <>
      <select
        value={current}
        disabled={isLoading || saving}
        onChange={(e) => void choose(e.target.value)}
        aria-label="prize card"
        style={{ marginTop: 4, fontSize: 12, maxWidth: 200 }}
      >
        {isLoading && <option value={current}>loading cards…</option>}
        {cards.map((c) => (
          <option key={c.slug} value={c.slug}>
            {c.emoji} {c.name} ({c.rarity})
          </option>
        ))}
      </select>
      {error && <Label style={{ color: '#dc2626', fontStyle: 'normal' }}>{error}</Label>}
    </>
  );
};

const PrizeCard: FC<{ dayKey: string; prize: QuizPrizePreviewDto; wide: boolean; isAdmin: boolean }> = ({
  dayKey,
  prize,
  wide,
  isAdmin,
}) => {
  const reduce = useReducedMotion();
  const [picking, setPicking] = useState(false);
  const w = wide ? 84 : 62;
  const h = wide ? 118 : 87;

  return (
    <Wrap aria-label={`the winner gets ${prize.cardName}`}>
      <motion.div
        key={prize.cardSlug}
        initial={reduce ? false : { opacity: 0, rotate: -12, y: 8 }}
        animate={{ opacity: 1, rotate: -4, y: 0 }}
        whileHover={reduce ? undefined : { rotate: 0, y: -3 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        style={{ flexShrink: 0 }}
      >
        <HandCard card={prizeFace(prize)} w={w} h={h} />
      </motion.div>
      <Text>
        <Label>the winner gets</Label>
        <Name>{prize.cardName}</Name>
        {prize.description && <Desc>{prize.description}</Desc>}
        {isAdmin &&
          (picking ? (
            <PrizePicker dayKey={dayKey} current={prize.cardSlug} onDone={() => setPicking(false)} />
          ) : (
            <Change type="button" onClick={() => setPicking(true)}>
              change
            </Change>
          ))}
      </Text>
    </Wrap>
  );
};

export default PrizeCard;
